import { QuickBasket } from '@virginexperiencedays/components-v2/src/drawers/QuickBasket';
import {
  type Props as QuickBasketContextProps,
  QuickBasketContextProvider,
} from '@virginexperiencedays/components-v2/src/drawers/QuickBasket/utils/QuickBasketContext';
import { Container } from '@virginexperiencedays/components-v2/src/layout/Container';
import { DyContextPageType } from '@virginexperiencedays/dy';
import { getCookieMap } from '@virginexperiencedays/web/utils/cookie';
import { useEffect, useState } from 'react';
import { trackGrc } from '../../../../libs/tracking/grc';
import { GrcCustomerState, GrcInteraction, GrcPageType } from '../../../../libs/tracking/types';
import Breadcrumbs from '../../../Breadcrumbs';
import DynamicYield from '../../../DynamicYield/DynamicYield';
import { trackRemovedFromCart as trackKlaviyoRemovedFromCart } from '../../../tracking/klaviyo';
import type { PageUIWrapperProps } from '../Page';
import Seo from '../SEO';
import StructuredData from '../StructuredData';
import { LOYALTY_KLAVIYO_EXTERNAL_ID_COOKIE } from '../../../../constants/cookie';

export function PageUIWrapper({
  product,
  isCollectionPages,
  seoPathOverride,
  isCollectionChoice,
  promocode,
  sku,
  reviewsList,
  isCollectionProduct,
  setQuickBasketOpen,
  handleUpdateBasketCount,
  quickBasketOpen,
  handleQuickBasketOpen,
  breadcrumbs,
  slugBreadcrumbNameMap,
  lvl2,
  lvl1,
  lvl0,
  children,
}: Readonly<PageUIWrapperProps>) {
  const breadcrumbsData = [
    ...breadcrumbs(lvl2 || lvl1 || lvl0, slugBreadcrumbNameMap),
    { name: product.title },
  ];

  const [hasGcContactId, setHasGcContactId] = useState<boolean>(false);

  useEffect(() => {
    if (process?.env?.NEXT_PUBLIC_FF_LOYALTY) {
      const cookieMap = getCookieMap();
      const contactId = cookieMap?.get(LOYALTY_KLAVIYO_EXTERNAL_ID_COOKIE);

      setHasGcContactId(!!contactId);
    }
  }, []);

  const handleLoyaltyClicks = (interaction: GrcInteraction) => {
    trackGrc({
      customerState: GrcCustomerState.Valid,
      interaction,
      pageType: GrcPageType.Pdp,
    });
  };

  const handleCallbackSetBasketItems: QuickBasketContextProps['callbackSetBasketItems'] = ({
    removedItem,
    basketItems,
    totalAmount,
  }) => {
    // track removed item from basket
    if (!removedItem) return;

    trackKlaviyoRemovedFromCart({
      categories: removedItem.categories,
      displayPrice: removedItem.displayPrice,
      imageUrl: removedItem.image?.src,
      qty: removedItem.qty,
      rrp: removedItem.rrp,
      sku: removedItem.sku,
      slug: removedItem.slug,
      title: removedItem.title,
      totalAmount: totalAmount ?? 0,
      url: removedItem.url,
      isCollectionProduct: removedItem.isCollectionProduct,
      items:
        basketItems?.map(
          ({ discountedPrice, displayPrice, image: { src: imageUrl }, ...item }) => ({
            ...item,
            displayPrice: discountedPrice ?? displayPrice ?? 0,
            imageUrl,
          }),
        ) ?? [],
    });
  };

  return (
    <>
      <Seo
        product={product}
        pagePathOverride={isCollectionPages ? seoPathOverride : undefined}
        metaRobots={isCollectionChoice ? 'noindex' : undefined}
      />
      <DynamicYield
        type={DyContextPageType.PRODUCT}
        data={[promocode ? `${sku}_${promocode}` : sku]}
      />
      <StructuredData
        product={product}
        reviews={reviewsList}
        pagePathOverride={isCollectionPages ? seoPathOverride : undefined}
        hideReviews={isCollectionProduct}
        hideOffers={isCollectionChoice}
      />

      <QuickBasketContextProvider
        setQuickBasketOpen={setQuickBasketOpen}
        handleQuickBasketItemCount={(n) => handleUpdateBasketCount(n)}
        callbackSetBasketItems={handleCallbackSetBasketItems}
      >
        <QuickBasket
          isOpen={quickBasketOpen}
          handleIsOpen={handleQuickBasketOpen}
          hasLoyaltyCta={hasGcContactId}
          onLoyaltyCheckoutClick={() => handleLoyaltyClicks(GrcInteraction.CheckoutNow)}
          onLoyaltyEditBasketClick={() => handleLoyaltyClicks(GrcInteraction.EditBasket)}
          onLoyaltyBannerClick={() => handleLoyaltyClicks(GrcInteraction.LoyaltyBanner)}
        />
        <Container>
          <Breadcrumbs asExternalLinks crumbs={breadcrumbsData} />
        </Container>

        {children}
      </QuickBasketContextProvider>
    </>
  );
}
